'use client';
import { FormEvent, useState } from 'react';
import styles from './filter.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faMagnifyingGlass,
	faX,
} from '@awesome.me/kit-012d1b9af0/icons/classic/regular';
import { Accordion } from '@chakra-ui/react';
import { useProperties } from '@/context/properties-context';
import { DataType, marked, other, outcomes, status, treatments } from './data';
import { injury, severity, time } from '@/components/main/search/data';
import PlacesAutocomplete from '@/components/main/search/autocomplete';
import { Field } from '@/components/main/field/field';
import { KeyMap, NestedKeys } from '@/types/properties';

export default function Filter() {
	const { properties, updateProperty } = useProperties();
	const [localQuery, setLocalQuery] = useState(properties.query);

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		updateProperty('query', localQuery);
	};

	const clearInput = () => {
		setLocalQuery('');
		updateProperty('query', '');
	};

	return (
		<div className={styles.filter}>
			{/* <div className={styles.search}> */}
			<form className={styles.search} onSubmit={handleSubmit}>
				<input
					type='text'
					placeholder='Search'
					value={localQuery}
					onChange={(e) => setLocalQuery(e.target.value)}
				/>
				<button>
					<FontAwesomeIcon
						icon={faMagnifyingGlass}
						size='lg'
						color='#446cb3'
						style={{ cursor: 'pointer' }}
					/>
				</button>
				{localQuery && (
					<FontAwesomeIcon
						icon={faX}
						size='lg'
						className={styles.clear}
						color={'#446cb3'}
						onClick={clearInput}
					/>
				)}
			</form>
			{/* </div> */}

			<PlacesAutocomplete
				label={false}
				placeholder='Location'
				className={styles['custom-location']}
			/>

			<div className=''>
				<Field>
					<Field.Content
						title={injury.title}
						labelValue={injury['label-value']}
						onFieldChange={(value) => updateProperty('injury', value)}
						value={properties.injury}
						onHome={false}
					/>
				</Field>
				<Field>
					<Field.Content
						title={severity.title}
						labelValue={severity['label-value']}
						onFieldChange={(value) => updateProperty('severity', value)}
						value={properties.severity}
						onHome={false}
					/>
				</Field>
				<Field>
					<Field.Content
						title={time.title}
						labelValue={time['label-value']}
						onFieldChange={(value) => updateProperty('time', value)}
						value={properties.time}
						onHome={false}
					/>
				</Field>
			</div>

			<Accord />
		</div>
	);
}

const Accord = () => {
	return (
		<Accordion.Root multiple defaultValue={[]}>
			{items.map((item) => (
				<Accordion.Item
					key={item.value}
					value={item.value}
					borderBottom={'none'}
				>
					<Accordion.ItemTrigger style={{ cursor: 'pointer' }}>
						<h1 className={styles['accord-title']}>{item.title}</h1>
						<Accordion.ItemIndicator color={'#446cb3'} padding={'1rem'} />
					</Accordion.ItemTrigger>
					<Accordion.ItemContent>
						<Accordion.ItemBody>{item.text}</Accordion.ItemBody>
					</Accordion.ItemContent>
				</Accordion.Item>
			))}
		</Accordion.Root>
	);
};

export const CheckBoxGroup = ({ data }: { data: DataType }) => {
	const { properties, updateNestedProperty } = useProperties();
	const [toggleAll, setToggleAll] = useState<boolean>(
		data.key.every(
			(item) =>
				properties[data.section][
					item.value as keyof (typeof properties)[typeof data.section]
				]
		)
	);

	const handleToggle = () => {
		setToggleAll((prev) => !prev);

		data.key.forEach((item) => {
			updateNestedProperty(data.section, item.value, !toggleAll);
		});
	};

	return (
		<div className={styles.CheckBoxGroup}>
			<div className={styles['CheckBoxGroup-all']} onClick={handleToggle}>
				<h2>{!toggleAll ? 'select all' : 'deselect all'}</h2>
			</div>
			<div className={styles['accord-check-grp']}>
				{data.key.map((item) => (
					<CheckBox
						key={item.value}
						label={item.label}
						section={data.section}
						keyName={item.value}
					/>
				))}
			</div>
		</div>
	);
};

export const CheckBox = <T extends NestedKeys>({
	label,
	section,
	keyName,
}: {
	label: string;
	section: NestedKeys;
	keyName: KeyMap[T];
}) => {
	const { properties, updateNestedProperty } = useProperties();

	return (
		<div className={styles['checkbox-container']}>
			<input
				type='checkbox'
				id={keyName}
				name={keyName}
				checked={
					!!properties[section][
						keyName as keyof (typeof properties)[typeof section]
					]
				}
				onChange={(e) =>
					updateNestedProperty(section, keyName, e.target.checked)
				}
			/>
			<label htmlFor={keyName}>{label}</label>
		</div>
	);
};

const items = [
	{
		title: treatments.title,
		value: treatments.section,
		text: <CheckBoxGroup data={treatments} />,
	},
	{
		title: outcomes.title,
		value: outcomes.section,
		text: <CheckBoxGroup data={outcomes} />,
	},
	{
		title: marked.title,
		value: marked.section,
		text: <CheckBoxGroup data={marked} />,
	},
	{
		title: status.title,
		value: status.section,
		text: <CheckBoxGroup data={status} />,
	},
	{
		title: other.title,
		value: other.section,
		text: <CheckBoxGroup data={other} />,
	},
];
